import React from 'react';
import { Heart, ShoppingCart, Eye, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { valentineGifts } from '../data/mock';

const ValentineGifts = () => {
  return (
    <section id="valentine" className="relative py-20 lg:py-28 overflow-hidden">
      {/* Valentine Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#2d1f2f] via-[#1e1919] to-[#2f1f1f]" />
      
      {/* Floating Hearts Background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <Heart
            key={i}
            className="absolute text-[#ff6b6b]/10 fill-current"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${15 + Math.random() * 40}px`,
              height: `${15 + Math.random() * 40}px`,
              transform: `rotate(${Math.random() * 360}deg)`,
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#ff6b6b]/20 backdrop-blur-sm border border-[#ff6b6b]/30 rounded-full px-6 py-3 mb-6">
            <Heart className="w-5 h-5 text-[#ff6b6b] fill-current animate-pulse" />
            <span className="text-[#ff6b6b] font-bold text-sm tracking-wider">VALENTINE'S COLLECTION</span>
            <Heart className="w-5 h-5 text-[#ff6b6b] fill-current animate-pulse" />
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Customizable <span className="bg-gradient-to-r from-[#ff6b6b] to-[#ff8fab] bg-clip-text text-transparent">Valentine Gifts</span>
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Show your love with personalized gifts. Upload your photos and create memories that last forever.
          </p>
        </div>

        {/* Gifts Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {valentineGifts.map((gift) => (
            <Card
              key={gift.id}
              className="group bg-gradient-to-br from-white/10 to-white/5 border-white/10 hover:border-[#ff6b6b]/50 transition-all duration-500 hover:shadow-2xl hover:shadow-[#ff6b6b]/20 overflow-hidden"
            >
              {/* Image Container */}
              <div className="relative aspect-square overflow-hidden">
                <img
                  src={gift.image}
                  alt={gift.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1e1919] via-transparent to-transparent" />
                
                {/* Overlay Actions */}
                <div className="absolute inset-0 bg-[#ff6b6b]/80 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center gap-4">
                  <Button size="icon" variant="ghost" className="bg-white/20 hover:bg-white/40 text-white rounded-full w-12 h-12">
                    <Eye className="w-5 h-5" />
                  </Button>
                  <Button size="icon" variant="ghost" className="bg-white/20 hover:bg-white/40 text-white rounded-full w-12 h-12">
                    <Heart className="w-5 h-5" />
                  </Button>
                  <Button size="icon" variant="ghost" className="bg-white/20 hover:bg-white/40 text-white rounded-full w-12 h-12">
                    <ShoppingCart className="w-5 h-5" />
                  </Button>
                </div>
                
                {/* Love Badge */}
                <div className="absolute top-3 right-3">
                  <div className="bg-[#ff6b6b] rounded-full p-2">
                    <Heart className="w-4 h-4 text-white fill-current" />
                  </div>
                </div>
              </div>
              
              <CardContent className="p-5">
                {/* Rating */}
                <div className="flex items-center gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-[#ffd700] fill-current" />
                  ))}
                  <span className="text-white/50 text-xs ml-2">(24 reviews)</span>
                </div>
                
                <h3 className="text-lg font-bold text-white mb-1 group-hover:text-[#ff6b6b] transition-colors">
                  {gift.name}
                </h3>
                <p className="text-white/50 text-sm mb-3">{gift.description}</p>
                
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-[#ffd700]">${gift.price}</span>
                  <Button size="sm" className="bg-[#ff6b6b] hover:bg-[#ff5252] text-white font-semibold transition-all duration-300">
                    Customize
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CTA Banner */}
        <div className="mt-16 relative rounded-3xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-[#ff6b6b] to-[#ff8fab]" />
          <div className="absolute inset-0 opacity-20">
            {[...Array(10)].map((_, i) => (
              <Heart
                key={i}
                className="absolute text-white fill-current"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  width: `${30 + Math.random() * 50}px`,
                  height: `${30 + Math.random() * 50}px`,
                }}
              />
            ))}
          </div>
          <div className="relative p-8 lg:p-12 text-center">
            <h3 className="text-2xl lg:text-4xl font-bold text-white mb-4">
              Make This Valentine Unforgettable
            </h3>
            <p className="text-white/90 text-lg max-w-2xl mx-auto mb-6">
              Order by February 10th for guaranteed Valentine's Day delivery at all 8 Gen-x locations!
            </p>
            <Button size="lg" className="bg-white hover:bg-white/90 text-[#ff6b6b] font-bold px-10 py-6 text-lg transition-all duration-300 hover:scale-105 shadow-2xl">
              <Heart className="w-5 h-5 mr-2 fill-current" />
              Shop Valentine Collection
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ValentineGifts;
